/**
 * CalendarView — orchestrator component for the board calendar view.
 * Manages view mode (month/week), navigation, and wires card data
 * from ActiveBoardContext to the appropriate grid component.
 *
 * Validates: Requirements 1.2, 1.3, 3.1, 3.2, 3.3, 3.5, 5.1, 6.1, 6.2, 6.3,
 *            6.4, 8.2, 8.3, 8.6, 9.3, 9.4, 9.5, 10.7
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MonthGrid } from './MonthGrid';
import { WeekGrid } from './WeekGrid';
import { UndatedIndicator } from './UndatedIndicator';
import {
  groupCardsByDate,
  computeDateSpans,
  addMonths,
  addWeeks,
} from '../../utils/date-utils';
import { createCalendarDragController } from '../../hooks/calendar-drag-controller';
import { useActiveBoard } from '../../state/active-board-context';
import type { CardSummary, ListSummary } from '../../state/active-board-context';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CalendarViewProps {
  readonly cards: readonly CardSummary[];
  readonly lists: readonly ListSummary[];
  readonly boardId: string;
  readonly isStandalone?: boolean;
}

type ViewMode = 'month' | 'week';

interface MonthDate {
  year: number;
  month: number; // 0-indexed
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get today's date info for initializing state. */
function getToday(): { year: number; month: number; isoDate: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = String(now.getDate()).padStart(2, '0');
  const mo = String(m + 1).padStart(2, '0');
  return { year: y, month: m, isoDate: `${y}-${mo}-${d}` };
}

/** Format month/year for display in header. */
function formatMonthYear(year: number, month: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${months[month]} ${year}`;
}

/** Format a week range label. */
function formatWeekRange(weekStartDate: string): string {
  const start = new Date(weekStartDate + 'T00:00:00');
  const end = new Date(start);
  end.setDate(end.getDate() + 6);

  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const startStr = start.toLocaleDateString('en-US', options);
  const endStr = end.toLocaleDateString('en-US', { ...options, year: 'numeric' });
  return `${startStr} – ${endStr}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CalendarView({
  cards,
  lists,
  boardId,
  isStandalone = false,
}: CalendarViewProps): React.ReactElement {
  const navigate = useNavigate();
  const { updateCard } = useActiveBoard();

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('month');

  // Navigation state
  const today = useMemo(() => getToday(), []);
  const [monthDate, setMonthDate] = useState<MonthDate>({
    year: today.year,
    month: today.month,
  });
  const [weekDate, setWeekDate] = useState<string>(today.isoDate);

  // Compute card groupings
  const cardsByDate = useMemo(() => groupCardsByDate(cards), [cards]);
  const dateSpans = useMemo(() => computeDateSpans(cards, lists), [cards, lists]);
  const undatedCount = useMemo(
    () => cards.filter((c) => c.dueDate === null).length,
    [cards],
  );

  // Drag controller
  const dragControllerRef = useRef(
    createCalendarDragController({
      async onReschedule(cardId, newDueDate, newStartDate) {
        try {
          await updateCard(boardId, {
            cardId,
            dueDate: newDueDate,
            startDate: newStartDate,
          });
          return { success: true };
        } catch {
          return { success: false };
        }
      },
    }),
  );

  // Navigation handlers
  const handlePrevMonth = useCallback(() => {
    setMonthDate((prev) => addMonths(prev.year, prev.month, -1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setMonthDate((prev) => addMonths(prev.year, prev.month, 1));
  }, []);

  const handlePrevWeek = useCallback(() => {
    setWeekDate((prev) => addWeeks(prev, -1));
  }, []);

  const handleNextWeek = useCallback(() => {
    setWeekDate((prev) => addWeeks(prev, 1));
  }, []);

  // Card click → navigate to card detail
  const handleCardClick = useCallback(
    (cardId: string) => {
      navigate(`/boards/${boardId}/cards/${cardId}`);
    },
    [navigate, boardId],
  );

  // Drop handler → reschedule card via drag controller
  const handleDrop = useCallback(
    (cardId: string, targetDate: string) => {
      const card = cards.find((c) => c.cardId === cardId);
      if (!card) return;

      const controller = dragControllerRef.current;
      controller.startDrag({
        cardId: card.cardId,
        originalDueDate: card.dueDate ?? targetDate,
        originalStartDate: card.startDate,
      });
      controller.updateTarget({ date: targetDate });
      controller.drop();
    },
    [cards],
  );

  // Week navigation callback for WeekGrid arrow keys
  const handleNavigateWeek = useCallback((delta: number) => {
    setWeekDate((prev) => addWeeks(prev, delta));
  }, []);

  // --- Keyboard reschedule state ---
  const rescheduleRef = useRef<{
    cardId: string;
    originalDueDate: string;
    originalStartDate: string | null;
    daysDelta: number;
  } | null>(null);

  const handleRescheduleMove = useCallback(
    (cardId: string, direction: 'left' | 'right') => {
      const card = cards.find((c) => c.cardId === cardId);
      if (!card) return;

      // Initialize reschedule tracking if not already in progress
      if (!rescheduleRef.current || rescheduleRef.current.cardId !== cardId) {
        rescheduleRef.current = {
          cardId,
          originalDueDate: card.dueDate ?? '',
          originalStartDate: card.startDate,
          daysDelta: 0,
        };
      }

      const delta = direction === 'right' ? 1 : -1;
      rescheduleRef.current.daysDelta += delta;

      // Compute the new target date and apply optimistically
      const originalDue = new Date(rescheduleRef.current.originalDueDate + 'T00:00:00');
      const targetDue = new Date(originalDue);
      targetDue.setDate(targetDue.getDate() + rescheduleRef.current.daysDelta);
      const targetDateStr = `${targetDue.getFullYear()}-${String(targetDue.getMonth() + 1).padStart(2, '0')}-${String(targetDue.getDate()).padStart(2, '0')}`;

      // Apply optimistic preview via drag controller
      const controller = dragControllerRef.current;
      controller.startDrag({
        cardId,
        originalDueDate: rescheduleRef.current.originalDueDate,
        originalStartDate: rescheduleRef.current.originalStartDate,
      });
      controller.updateTarget({ date: targetDateStr });
    },
    [cards],
  );

  const handleRescheduleConfirm = useCallback(
    (cardId: string) => {
      if (!rescheduleRef.current || rescheduleRef.current.cardId !== cardId) return;
      if (rescheduleRef.current.daysDelta === 0) {
        rescheduleRef.current = null;
        return;
      }

      const originalDue = new Date(rescheduleRef.current.originalDueDate + 'T00:00:00');
      const targetDue = new Date(originalDue);
      targetDue.setDate(targetDue.getDate() + rescheduleRef.current.daysDelta);
      const targetDateStr = `${targetDue.getFullYear()}-${String(targetDue.getMonth() + 1).padStart(2, '0')}-${String(targetDue.getDate()).padStart(2, '0')}`;

      // Perform the reschedule via drag controller
      const controller = dragControllerRef.current;
      controller.startDrag({
        cardId,
        originalDueDate: rescheduleRef.current.originalDueDate,
        originalStartDate: rescheduleRef.current.originalStartDate,
      });
      controller.updateTarget({ date: targetDateStr });
      controller.drop();

      rescheduleRef.current = null;
    },
    [],
  );

  const handleRescheduleCancel = useCallback(
    (cardId: string) => {
      if (rescheduleRef.current?.cardId === cardId) {
        const controller = dragControllerRef.current;
        controller.cancel();
        rescheduleRef.current = null;
      }
    },
    [],
  );

  // View mode toggle
  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
  }, []);

  // Header label
  const headerLabel =
    viewMode === 'month'
      ? formatMonthYear(monthDate.year, monthDate.month)
      : formatWeekRange(weekDate);

  // Navigation aria-labels
  const prevLabel =
    viewMode === 'month' ? 'Go to previous month' : 'Go to previous week';
  const nextLabel =
    viewMode === 'month' ? 'Go to next month' : 'Go to next week';

  return (
    <div className="flex flex-col h-full">
      {/* Header area */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          {/* Navigation controls */}
          <button
            type="button"
            onClick={viewMode === 'month' ? handlePrevMonth : handlePrevWeek}
            aria-label={prevLabel}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-5 h-5"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          <h2 className="text-lg font-semibold text-foreground min-w-[12rem] text-center">
            {headerLabel}
          </h2>

          <button
            type="button"
            onClick={viewMode === 'month' ? handleNextMonth : handleNextWeek}
            aria-label={nextLabel}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-5 h-5"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="flex items-center gap-3">
          {/* Undated indicator */}
          <UndatedIndicator count={undatedCount} />

          {/* Month/Week toggle — hidden in standalone mode */}
          {!isStandalone && (
            <div className="inline-flex h-8 items-center rounded-md bg-muted p-0.5 text-sm">
              <button
                type="button"
                onClick={() => handleViewModeChange('month')}
                aria-pressed={viewMode === 'month'}
                className={`inline-flex items-center justify-center rounded-sm px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  viewMode === 'month'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground'
                }`}
              >
                Month
              </button>
              <button
                type="button"
                onClick={() => handleViewModeChange('week')}
                aria-pressed={viewMode === 'week'}
                className={`inline-flex items-center justify-center rounded-sm px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  viewMode === 'week'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground'
                }`}
              >
                Week
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Grid area */}
      <div className="flex-1 overflow-auto p-4">
        {viewMode === 'month' ? (
          <MonthGrid
            year={monthDate.year}
            month={monthDate.month}
            cardsByDate={cardsByDate}
            dateSpans={dateSpans}
            lists={lists}
            onDrop={handleDrop}
            onCardClick={handleCardClick}
            onRescheduleMove={handleRescheduleMove}
            onRescheduleConfirm={handleRescheduleConfirm}
            onRescheduleCancel={handleRescheduleCancel}
          />
        ) : (
          <WeekGrid
            weekStartDate={weekDate}
            cardsByDate={cardsByDate}
            dateSpans={dateSpans}
            lists={lists}
            onDrop={handleDrop}
            onCardClick={handleCardClick}
            onNavigateWeek={handleNavigateWeek}
            onRescheduleMove={handleRescheduleMove}
            onRescheduleConfirm={handleRescheduleConfirm}
            onRescheduleCancel={handleRescheduleCancel}
          />
        )}
      </div>
    </div>
  );
}
